import { flatten } from "@turf/turf";
import { type MutableRefObject, useEffect } from "react";
import {
  mutliPolygonGeometrySchema,
  polygonGeometrySchema,
} from "src/components/map/location-bed-polygons/parse-geometry";
import { stringToJSONSchema } from "src/utils/zod";
import { z } from "zod";
import { type MapMode } from "./types";

const schema = stringToJSONSchema.pipe(
  z.union([
    polygonGeometrySchema,
    mutliPolygonGeometrySchema,
    z
      .object({
        type: z.literal("Feature"),
        geometry: z.union([polygonGeometrySchema, mutliPolygonGeometrySchema]),
        properties: z.record(z.unknown()),
      })
      .passthrough(),
    z
      .object({
        type: z.literal("FeatureCollection"),
        features: z.array(
          z
            .object({
              type: z.literal("Feature"),
              geometry: z.union([
                polygonGeometrySchema,
                mutliPolygonGeometrySchema,
              ]),
              properties: z.record(z.unknown()),
            })
            .passthrough(),
        ),
      })
      .passthrough(),
  ]),
);

export const useMapPaste = ({
  drawRef,
  mode,
}: {
  drawRef: MutableRefObject<MapboxDraw | undefined>;
  mode: MapMode;
}) => {
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (mode !== "editLocation") {
        return;
      }

      const paste = event.clipboardData?.getData("text");

      if (paste == null) {
        return;
      }

      const geojson = schema.safeParse(paste);

      if (geojson.success) {
        drawRef.current?.add(flatten(geojson.data));
        drawRef.current?.changeMode("static");
      }
    };

    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [drawRef, mode]);
};
